/** Mock consultations — mirrors consultationsApi.js for offline/dev work. */
const delay = (ms = 220) => new Promise((r) => setTimeout(r, ms));

const DAY = 864e5;
const mkErr = (status, code, message) => {
  const err = new Error(message || code);
  err.response = { status, data: { error: { code, message, messageEn: message, details: {} } } };
  return err;
};

/**
 * RULE 4 — the platform directory. Unavailable doctors are included with a
 * reason so the picker is never an empty dead end.
 */
const _physicians = [
  {
    phone: '+989120000001', fullName: 'دکتر سارا مرادی',
    title: { fa: 'متخصص پوست و مو', en: 'Dermatologist' },
    specialties: ['botox', 'filler', 'skin'],
    bio: { fa: 'ده سال سابقه در زیبایی و جوان‌سازی پوست.', en: '10 years in aesthetic dermatology.' },
    avatarUrl: null, available: true, unavailableReason: null, clinicCodes: ['100200'],
  },
  {
    phone: '+989120000002', fullName: 'دکتر امیر رضایی',
    title: { fa: 'جراح پلاستیک', en: 'Plastic Surgeon' },
    specialties: ['face_analysis', 'botox'],
    bio: { fa: 'جراح فک و صورت.', en: 'Maxillofacial surgeon.' },
    avatarUrl: null, available: true, unavailableReason: null, clinicCodes: [],
  },
  {
    phone: '+989120000003', fullName: 'دکتر نگار حسینی',
    title: { fa: 'متخصص زیبایی', en: 'Aesthetic Physician' },
    specialties: ['skin'],
    bio: { fa: '', en: '' },
    avatarUrl: null, available: false, unavailableReason: 'AT_CAPACITY', clinicCodes: [],
  },
];

let _consultations = [
  {
    // Capture-first, mid-flow: photos taken, nothing chosen or paid yet. This
    // is the state the free stage actually produces, so the mock exercises
    // preview -> physician -> service -> pay rather than the legacy path.
    id: 'cons-0', clinicCode: null, clinicName: null, origin: 'purchase',
    status: 'awaiting_purchase', serviceId: null,
    serviceName: null, priceToman: null,
    physicianName: null, analysisId: 'mock-capture-1', aiReadyAt: null, confirmedAt: null,
    isPaid: false, freeCapture: true, hasCapture: true, autoAssigned: false,
    createdAt: new Date(Date.now() - 0.2 * DAY).toISOString(), unreadCount: 0,
    chatState: 'not_open', chatOpensAt: null, chatExpiresAt: null, wasSuperseded: false,
  },
  {
    id: 'cons-1', clinicCode: '100200', clinicName: { fa: 'کلینیک زیبایی نور', en: 'Noor Clinic' },
    origin: 'purchase',
    status: 'ai_preliminary', serviceId: 'botox', serviceName: { fa: 'بوتاکس', en: 'Botox' },
    physicianName: 'دکتر سارا مرادی', analysisId: 'cap-a',
    aiReadyAt: new Date(Date.now() - 3600e3).toISOString(),
    confirmedAt: null, physicianNote: null, disposition: null,
    createdAt: new Date(Date.now() - 2 * DAY).toISOString(), unreadCount: 0,
    chatState: 'not_open', chatOpensAt: null, chatExpiresAt: null, wasSuperseded: false,
  },
  {
    id: 'cons-2', clinicCode: '100200', clinicName: { fa: 'کلینیک زیبایی نور', en: 'Noor Clinic' },
    origin: 'purchase',
    status: 'confirmed', serviceId: 'filler', serviceName: { fa: 'فیلر', en: 'Filler' },
    physicianName: 'دکتر امیر رضایی', analysisId: 'cap-b',
    aiReadyAt: new Date(Date.now() - 9 * DAY).toISOString(),
    confirmedAt: new Date(Date.now() - 2 * DAY).toISOString(),
    physicianNote: 'نتایج آنالیز بررسی و تأیید شد. برای ادامه، یک جلسه حضوری توصیه می‌شود.',
    disposition: 'confirmed',
    createdAt: new Date(Date.now() - 10 * DAY).toISOString(), unreadCount: 1,
    chatState: 'open',
    chatOpensAt: new Date(Date.now() - 2 * DAY).toISOString(),
    chatExpiresAt: new Date(Date.now() + 5 * DAY).toISOString(),
    wasSuperseded: true,
  },
  {
    id: 'cons-3', clinicCode: null, clinicName: null, origin: 'purchase',
    status: 'confirmed', serviceId: 'botox', serviceName: { fa: 'بوتاکس', en: 'Botox' },
    physicianName: 'دکتر سارا مرادی', analysisId: 'cap-c',
    aiReadyAt: new Date(Date.now() - 40 * DAY).toISOString(),
    confirmedAt: new Date(Date.now() - 39 * DAY).toISOString(),
    physicianNote: 'گزارش تأیید شد.', disposition: 'confirmed',
    createdAt: new Date(Date.now() - 42 * DAY).toISOString(), unreadCount: 0,
    chatState: 'closed',
    chatOpensAt: new Date(Date.now() - 39 * DAY).toISOString(),
    chatExpiresAt: new Date(Date.now() - 32 * DAY).toISOString(),
    wasSuperseded: true,
  },
];

const _messages = {
  'cons-2': [{
    id: 'm1', author: 'physician', authorName: 'دکتر امیر رضایی',
    body: 'سلام، گزارش شما را تأیید کردم. اگر سؤالی دارید بپرسید.',
    createdAt: new Date(Date.now() - 2 * DAY).toISOString(),
  }],
  'cons-3': [],
};

export async function listPhysicians() { await delay(); return _physicians; }

export async function startConsultation(serviceId) {
  await delay(320);
  const existing = _consultations.find(
    (c) => c.serviceId === serviceId && ['draft_capture', 'awaiting_purchase'].includes(c.status));
  if (existing) return existing;
  const c = {
    id: `cons-${Date.now()}`, clinicCode: null, clinicName: null, origin: 'purchase',
    status: 'draft_capture', serviceId,
    serviceName: { fa: 'بوتاکس', en: 'Botox' },
    physicianName: null, analysisId: null, inviteId: null,   // route 2 uses no link
    aiReadyAt: null, confirmedAt: null, createdAt: new Date().toISOString(),
    unreadCount: 0, chatState: 'not_open', chatOpensAt: null, chatExpiresAt: null,
    wasSuperseded: false, isPaid: false, freeCapture: true, hasCapture: false,
    autoAssigned: false, retentionDays: 14,
    captureExpiresAt: new Date(Date.now() + 14 * DAY).toISOString(),
  };
  _consultations = [c, ..._consultations];
  return c;
}

export async function choosePhysicianForMe(id) {
  await delay(360);
  const c = _consultations.find((x) => x.id === id);
  if (!c) throw mkErr(404, 'CONSULTATION_NOT_FOUND', 'مشاوره یافت نشد.');
  const doc = _physicians.find((p) => p.available);
  if (!doc) throw mkErr(503, 'NO_PHYSICIAN_AVAILABLE', 'در حال حاضر پزشکی در دسترس نیست.');
  c.physicianName = doc.fullName;
  c.autoAssigned = true;
  if (!['draft_capture', 'awaiting_purchase'].includes(c.status)) c.status = 'awaiting_capture';
  return c;
}

export async function getPreview() {
  await delay(280);
  // 68 points in a rough face arrangement, enough to look like a real capture
  const pts = [];
  for (let i = 0; i < 68; i += 1) {
    const a = (i / 68) * Math.PI * 2;
    pts.push({ x: 0.5 + 0.26 * Math.cos(a), y: 0.5 + 0.34 * Math.sin(a) });
  }
  [[0.40,0.42],[0.60,0.42],[0.50,0.52],[0.50,0.60],[0.42,0.66],[0.58,0.66]]
    .forEach(([x,y]) => pts.push({ x, y }));
  return {
    capturedPoses: 2, landmarkCount: pts.length,
    frames: [{
      pose: 'neutral', imageSize: { width: 512, height: 512 },
      count: pts.length, landmarks: pts,
      // a flat placeholder stands in for the real capture offline
      image: 'data:image/svg+xml;utf8,' + encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">' +
        '<rect width="512" height="512" fill="#EFEAE1"/>' +
        '<ellipse cx="256" cy="250" rx="120" ry="155" fill="#E4D2BA"/>' +
        '<circle cx="212" cy="225" r="10" fill="#7A6144"/>' +
        '<circle cx="300" cy="225" r="10" fill="#7A6144"/>' +
        '<path d="M215 310q41 30 82 0" stroke="#B08A6A" stroke-width="8" fill="none"/></svg>'),
    }],
    agents: [
      {
        key: 'tern_plus',
        name: { fa: 'مدل چهره تِرن پلاس', en: 'Tern Plus Face Model' },
        description: { fa: 'حرکت عضلات چهره‌ی شما را در حالت‌های مختلف می‌خواند.',
                       en: 'Reads how your expression muscles move across each pose.' },
        measureCount: 7,
        measures: [
          { k: { fa: 'نقشه‌ی حالت‌ها', en: 'Expression map' },
            v: { fa: 'اینکه کدام عضلات لبخند، اخم و بالا بردن ابروی شما را رهبری می‌کنند.',
                 en: 'Which muscles lead your smile, your frown and your brow lift.' } },
          { k: { fa: 'هماهنگی حرکت', en: 'Movement harmony' },
            v: { fa: 'اینکه دو سمت چهره‌ی شما چقدر هم‌آهنگ با هم حرکت می‌کنند.',
                 en: 'How closely your two sides move together.' } },
          { k: { fa: 'آرامش در سکون', en: 'Calm at rest' },
            v: { fa: 'اینکه چهره‌ی شما در حالت آرام چقدر رها می‌نشیند.',
                 en: 'How softly your face settles when it is at rest.' } },
        ],
      },
      {
        key: 'serval_plus',
        name: { fa: 'مدل چهره سِروال پلاس', en: 'Serval Plus Face Model' },
        description: { fa: 'حجم و منحنی‌های چهره‌ی شما و مسیر نور روی آن‌ها را می‌خواند.',
                       en: 'Reads the volumes and curves of your face, and how light travels over them.' },
        measureCount: 7,
        measures: [
          { k: { fa: 'نقشه‌ی حجم', en: 'Volume map' },
            v: { fa: 'جایی که نور به‌طور طبیعی روی گونه و شقیقه‌ی شما جمع می‌شود.',
                 en: 'Where light naturally gathers across your cheeks and temples.' } },
          { k: { fa: 'جریان منحنی', en: 'Contour flow' },
            v: { fa: 'مسیر پیوسته‌ی خط از استخوان گونه تا خط فک.',
                 en: 'The unbroken line from cheekbone down to jaw.' } },
          { k: { fa: 'تناسب لب', en: 'Lip harmony' },
            v: { fa: 'نسبت لب بالا به لب پایین و قاب آن‌ها در چهره.',
                 en: 'How your upper and lower lip sit in proportion, and how they frame.' } },
        ],
      },
      {
        key: 'lynx_plus',
        name: { fa: 'مدل چهره لینکس پلاس', en: 'Lynx Plus Face Model' },
        description: { fa: 'ساختار و وضوح خطوط چهره‌ی شما را می‌خواند.',
                       en: 'Reads the structure of your face and how crisply its lines read.' },
        measureCount: 7,
        measures: [
          { k: { fa: 'وضوح خط فک', en: 'Jawline definition' },
            v: { fa: 'اینکه خط فک شما از گوش تا چانه چقدر روشن خوانده می‌شود.',
                 en: 'How clearly your jaw reads from ear to chin.' } },
          { k: { fa: 'تکیه‌گاه میان‌چهره', en: 'Midface support' },
            v: { fa: 'اینکه حجم گونه چگونه روی استخوان می‌نشیند.',
                 en: 'How your cheek volume rests over the bone beneath it.' } },
          { k: { fa: 'زاویه‌ی فک و گردن', en: 'Jaw-to-neck angle' },
            v: { fa: 'خطی که فک شما را از گردن جدا می‌کند.',
                 en: 'The line that sets your jaw apart from your neck.' } },
        ],
      },
    ],
  };
}

export async function setConsultationService(id, serviceId) {
  await delay(240);
  const c = _consultations.find((x) => x.id === id);
  if (!c) throw mkErr(404, 'CONSULTATION_NOT_FOUND', 'مشاوره یافت نشد.');
  c.serviceId = serviceId;
  c.serviceName = { fa: 'بوتاکس', en: 'Botox' };
  // The real serializer reads this off the catalog; the mock needs a figure so
  // the discount preview has something to quote against.
  c.priceToman = 3500000;
  return c;
}

export async function payConsultation(id, gateway, discountCode) {
  void discountCode; // priced server-side in real mode; nothing to charge here
  await delay(420);
  const c = _consultations.find((x) => x.id === id);
  if (!c) throw mkErr(404, 'CONSULTATION_NOT_FOUND', 'مشاوره یافت نشد.');
  // Mirrors PAYMENT_BYPASS on the server: settles immediately, no gateway.
  c.isPaid = true;
  c.freeCapture = false;
  c.status = c.analysisId ? 'awaiting_ai' : 'awaiting_capture';
  // mirrors the server: a named gateway returns a redirect instead of settling
  if (gateway) {
    return { paymentUrl: `https://example.invalid/${gateway}/pay`, gateway };
  }
  return { bypassed: true, consultation: c };
}

export async function getConsultationTerms() {
  await delay(120);
  return { gateways: ['zarinpal', 'sep'], paymentBypass: false,
           slaHours: 48, chatDays: 7, unpaidCaptureRetentionDays: 14,
           physicianCount: _physicians.length, captureBeforePayment: true };
}

export async function choosePhysician(id, physicianPhone) {
  await delay(320);
  const c = _consultations.find((x) => x.id === id);
  if (!c) throw mkErr(404, 'CONSULTATION_NOT_FOUND', 'مشاوره یافت نشد.');
  // Mirrors PHYSICIAN_EDITABLE on the server. Omitting the pre-purchase
  // statuses made the mock reject exactly the case the free stage produces.
  if (!['draft_capture', 'awaiting_purchase', 'awaiting_physician', 'awaiting_capture']
        .includes(c.status)) {
    throw mkErr(409, 'PHYSICIAN_LOCKED', 'در این مرحله امکان تغییر پزشک وجود ندارد.');
  }
  const doc = _physicians.find((p) => p.phone === physicianPhone);
  if (!doc) throw mkErr(404, 'PHYSICIAN_NOT_FOUND', 'پزشک یافت نشد.');
  if (!doc.available) {
    throw mkErr(409, 'PHYSICIAN_UNAVAILABLE',
      'این پزشک در حال حاضر پذیرش ندارد. لطفاً پزشک دیگری انتخاب کنید.');
  }
  c.physicianName = doc.fullName;
  // A pre-purchase consultation stays pre-purchase: choosing a doctor is not
  // what moves it forward, paying is.
  if (!['draft_capture', 'awaiting_purchase'].includes(c.status)) {
    c.status = 'awaiting_capture';
    c.inviteId = 'inv-mock-1';
  }
  return c;
}

export async function listConsultations() { await delay(); return _consultations; }

export async function getConsultation(id) {
  await delay();
  return _consultations.find((c) => c.id === id) || null;
}

export async function getConsultationReport(id, { draft = false } = {}) {
  await delay();
  const c = _consultations.find((x) => x.id === id);
  if (!c) return { reportStatus: 'processing' };
  if (['draft_capture', 'awaiting_purchase'].includes(c.status)) {
    return { reportStatus: 'awaiting_purchase', hasCapture: !!c.analysisId,
             physicianName: c.physicianName };
  }
  if (['confirmed', 'needs_in_person'].includes(c.status)) {
    return {
      reportStatus: 'confirmed',
      html: '<h2>گزارش تأییدشده</h2><p>نتایج آنالیز چهره پس از بررسی پزشک تأیید شد.</p>',
      summary: { fa: 'گزارش تأییدشده توسط پزشک.', en: 'گزارش تأییدشده توسط پزشک.' },
      physicianName: c.physicianName, physicianNote: c.physicianNote,
      disposition: c.disposition, confirmedAt: c.confirmedAt,
      wasSuperseded: c.wasSuperseded,
      amended: { summary: true, note: true },
      chatState: c.chatState, chatExpiresAt: c.chatExpiresAt,
    };
  }
  if (c.status === 'ai_failed') {
    return { reportStatus: 'failed', physicianName: c.physicianName };
  }
  if (c.status === 'ai_preliminary' && !draft) {
    // Progress by default; the draft is opt-in.
    return { reportStatus: 'reviewing', draftAvailable: true, slaHours: 48,
             generatedAt: c.aiReadyAt, physicianName: c.physicianName };
  }
  if (c.status === 'ai_preliminary') {
    return {
      reportStatus: 'preliminary',
      html: '<h2>گزارش اولیه هوش مصنوعی</h2><p>این نسخه هنوز توسط پزشک بررسی نشده است.</p>',
      summary: { fa: 'پیش‌نویس تحلیل هوش مصنوعی.', en: 'پیش‌نویس تحلیل هوش مصنوعی.' },
      generatedAt: c.aiReadyAt, physicianName: c.physicianName,
    };
  }
  return { reportStatus: 'processing', physicianName: c.physicianName };
}

export async function listMessages(id) {
  await delay();
  const c = _consultations.find((x) => x.id === id) || {};
  return {
    chatState: c.chatState || 'not_open',
    chatOpensAt: c.chatOpensAt || null,
    chatExpiresAt: c.chatExpiresAt || null,
    messages: _messages[id] || [],
  };
}

export async function sendMessage(id, body) {
  await delay();
  const c = _consultations.find((x) => x.id === id);
  // RULE 6 — the window binds the client too; mirror the server's 409s.
  if (!c || c.chatState === 'not_open') {
    throw mkErr(409, 'CHAT_NOT_OPEN', 'گفت‌وگو پس از تأیید گزارش توسط پزشک فعال می‌شود.');
  }
  if (c.chatState === 'closed') {
    throw mkErr(409, 'CHAT_WINDOW_CLOSED', 'مهلت گفت‌وگو با پزشک به پایان رسیده است.');
  }
  const m = { id: `m${Date.now()}`, author: 'client', body, createdAt: new Date().toISOString() };
  _messages[id] = [...(_messages[id] || []), m];
  _consultations = _consultations.map((x) => (x.id === id ? { ...x, unreadCount: 0 } : x));
  return m;
}

/**
 * Evidence for the visual report: a schematic face and the measure map.
 *
 * The landmarks here are a drawn diagram, not a photograph — enough for the
 * overlay, the stepper and the pose switcher to be exercised offline without
 * putting a stand-in person's face in the mock.
 */
const MOCK_POINTS = {
  // Midline: hairline, glabella, under the nose, chin.
  10: [0.50, 0.14], 9: [0.50, 0.33], 168: [0.50, 0.345], 2: [0.50, 0.62], 152: [0.50, 0.88],
  // The agent's symmetric pairs, by area, so the colour map has every area to
  // colour: eyes, brows, nose, mouth, cheeks, jaw, temples.
  33: [0.36, 0.42], 263: [0.64, 0.42],       // outer eye corners
  133: [0.445, 0.425], 362: [0.555, 0.425],  // inner eye corners
  159: [0.41, 0.40], 386: [0.59, 0.40],      // upper lids
  145: [0.41, 0.46], 374: [0.59, 0.46],      // lower lids
  70: [0.33, 0.33], 300: [0.67, 0.33],       // outer brows
  105: [0.41, 0.31], 334: [0.59, 0.31],      // mid brows
  107: [0.46, 0.32], 336: [0.54, 0.32],      // inner brows
  64: [0.465, 0.58], 294: [0.535, 0.58],     // nostrils
  61: [0.42, 0.72], 291: [0.58, 0.72],       // mouth corners
  185: [0.455, 0.705], 409: [0.545, 0.705],  // upper lip sides
  146: [0.455, 0.745], 375: [0.545, 0.745],  // lower lip sides
  50: [0.335, 0.53], 280: [0.665, 0.53],     // cheekbones
  234: [0.295, 0.60], 454: [0.705, 0.60],    // jaw angles
  172: [0.355, 0.76], 397: [0.645, 0.76],    // lower jaw
  127: [0.285, 0.38], 356: [0.715, 0.38],    // temples
};

/** The agent's pairs in its own order, left index first — what the symmetry
 *  and muscle measures are built from. */
const MOCK_PAIRS = [
  [263, 33], [362, 133], [386, 159], [374, 145],
  [300, 70], [334, 105], [336, 107],
  [294, 64],
  [291, 61], [409, 185], [375, 146],
  [280, 50],
  [454, 234], [397, 172],
  [356, 127],
];

function mockLandmarks(shift = 0) {
  const max = Math.max(...Object.keys(MOCK_POINTS).map(Number));
  return Array.from({ length: max + 1 }, (_, i) => {
    const p = MOCK_POINTS[i];
    return p ? { x: p[0], y: p[1] + shift } : { x: 0.5, y: 0.5 };
  });
}

/** A plain SVG face, as a data URL, so the mock needs no binary asset. */
function mockFace(w, h, shift = 0) {
  const X = (n) => (n * w).toFixed(1);
  const Y = (n) => ((n + shift) * h).toFixed(1);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
    <rect width="${w}" height="${h}" fill="#EEE6DA"/>
    <ellipse cx="${X(0.5)}" cy="${Y(0.52)}" rx="${X(0.24)}" ry="${Y(0.42)}"
      fill="none" stroke="#786955" stroke-width="3"/>
    <circle cx="${X(0.41)}" cy="${Y(0.43)}" r="${X(0.035)}" fill="#463D2C"/>
    <circle cx="${X(0.59)}" cy="${Y(0.43)}" r="${X(0.035)}" fill="#463D2C"/>
    <path d="M${X(0.42)} ${Y(0.72)} Q ${X(0.5)} ${Y(0.76)} ${X(0.58)} ${Y(0.72)}"
      fill="none" stroke="#96615A" stroke-width="4"/>
    <line x1="${X(0.5)}" y1="${Y(0.34)}" x2="${X(0.5)}" y2="${Y(0.62)}"
      stroke="#786955" stroke-width="2"/>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export async function getEvidence() {
  await delay(220);
  return {
    agentKey: 'tern',
    poseCount: 3,
    frames: [
      { pose: 'neutral', image: mockFace(720, 720), imageSize: { width: 720, height: 720 },
        landmarks: mockLandmarks() },
      { pose: 'brows_up', image: mockFace(720, 960, -0.015),
        imageSize: { width: 720, height: 960 }, landmarks: mockLandmarks(-0.015) },
      { pose: 'smile', image: mockFace(720, 720, 0.012),
        imageSize: { width: 720, height: 720 }, landmarks: mockLandmarks(0.012) },
    ],
    measures: [
      { key: 'thirds', label: 'Facial thirds', view: 'frontal', poses: ['neutral'],
        points: [10, 168, 2, 152], lines: [[10, 168], [168, 2], [2, 152]] },
      { key: 'symmetry', label: 'Facial symmetry', view: 'frontal', poses: ['neutral'],
        points: MOCK_PAIRS.flat(), lines: MOCK_PAIRS.map((p) => [...p]) },
      { key: 'browEye', label: 'Brow & eye geometry', view: 'frontal',
        poses: ['neutral'],
        points: [334, 386, 374, 336, 105, 159, 145, 107],
        lines: [[334, 386], [386, 374], [105, 159], [159, 145], [336, 107]] },
      // The agent gives every muscle measure the full paired-point set and
      // names no area, which is exactly the case the drawing has to cope with:
      // the bot's descriptor says which area each muscle is read around, and
      // the rest of the points fall back to faint context.
      { key: 'muscle:frontalis', label: 'Frontalis (forehead lift)', group: 'muscles',
        view: 'frontal', poses: ['neutral', 'brows_up'],
        points: MOCK_PAIRS.flat(), lines: MOCK_PAIRS.map((p) => [...p]) },
      { key: 'muscle:zygomaticus', label: 'Zygomaticus (smile)', group: 'muscles',
        view: 'frontal', poses: ['neutral', 'smile'],
        points: MOCK_PAIRS.flat(), lines: MOCK_PAIRS.map((p) => [...p]) },
    ],
    // Everything the agent recorded besides the measure map. `regions` is what
    // the symmetry drawing is coloured by — the same per-area scores the
    // written report prints — and the spread here covers all three bands.
    metrics: {
      overallSymmetry: 87.3,
      lowerSide: 'left',
      regions: {
        brows: 91.2, temples: 88.0, eyes: 93.4, nose: 79.5,
        cheeks: 84.0, mouth: 71.8, jaw: 86.6,
      },
      topZone: 'Frontalis',
      interest: ['Frontalis', 'Zygomaticus'],
      poseCount: 3,
    },
  };
}

export const consultationsApiMock = {
  startConsultation, getPreview, setConsultationService, payConsultation, listPhysicians, choosePhysician, choosePhysicianForMe,
  listConsultations, getConsultation, getConsultationReport,
  getConsultationTerms, listMessages, sendMessage,
  getEvidence,
};
export default consultationsApiMock;
